import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number | string;
  company_name: string;
  HSN_SAC?: string;
  GST_SLAB?: string;
  [key: string]: any;
}

interface Customer {
  id: number | string;
  company_name: string;
  [key: string]: any;
}

function parseGSTSLAB(data: string | undefined): string[] {
  if (!data) return [];
  try { const arr = JSON.parse(data); return Array.isArray(arr) ? arr : []; } catch { return []; }
}

function parseHSNSAC(data: string | undefined): string[] {
  if (!data) return [];
  try { const arr = JSON.parse(data); return Array.isArray(arr) ? arr : []; } catch { return []; }
}

const RecurringInvoices: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [recurringList, setRecurringList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // Form state
  const [companyId, setCompanyId] = useState(() => {
    const s = localStorage.getItem("selected_company_id");
    return s && s !== "all" ? s : "";
  });
  const [customerId, setCustomerId] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [particulars, setParticulars] = useState("");
  const [hsnSac, setHsnSac] = useState("");
  const [amount, setAmount] = useState("");
  const [gstRate, setGstRate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isGstInclusive, setIsGstInclusive] = useState(false);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);

  // User auth state
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userCompanyId, setUserCompanyId] = useState<string | number | null>(null);

  // Helper function to decode JWT token
  const decodeToken = (token: any) => {
    if (!token) return {};
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map(function (c) {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return {};
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [compRes, custRes, recRes] = await Promise.all([
        axios.get(`${API_URL}/auth/companies`),
        axios.get(`${API_URL}/auth/customers`),
        axios.get(`${API_URL}/auth/recurring-invoices`),
      ]);
      const activeWorkspaceId = localStorage.getItem("selected_company_id");
      const allComps = compRes.data || [];
      if (activeWorkspaceId && activeWorkspaceId !== "all") {
        setCompanies(allComps.filter((c: any) => String(c.id) === String(activeWorkspaceId)));
        setCompanyId(String(activeWorkspaceId));
      } else {
        setCompanies(allComps);
      }
      setCustomers(custRes.data);
      setRecurringList(recRes.data);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedRole = localStorage.getItem("role");
    let decoded: any = null;
    if (token) {
      decoded = decodeToken(token);
    }
    setUserRole(storedRole || null);

    if (decoded) {
      const compId = decoded.userId || decoded.id || decoded.companyId || decoded.company_id || decoded.cid;
      setUserCompanyId(compId ? String(compId) : null);
    } else {
      setUserCompanyId(null);
    }

    fetchData();
  }, []);

  const selectedCompany = companies.find(c => String(c.id) === companyId);
  const gstSlabOptions = selectedCompany ? parseGSTSLAB(selectedCompany.GST_SLAB) : [];
  const hsnOptions = selectedCompany ? parseHSNSAC(selectedCompany.HSN_SAC) : [];

  const getCompanyName = (id: any) => companies.find(c => String(c.id) === String(id))?.company_name || "—";
  const getCustomerName = (id: any) => customers.find(c => String(c.id) === String(id))?.company_name || "—";

  const handleEdit = (rec: any) => {
    setEditId(rec.id);
    setCompanyId(String(rec.Company_id));
    setCustomerId(String(rec.Customer_id));
    setFrequency(rec.frequency);
    
    // YYYY-MM-DD parsing for dates
    const start = rec.start_date ? new Date(rec.start_date).toISOString().split('T')[0] : "";
    const end = rec.end_date ? new Date(rec.end_date).toISOString().split('T')[0] : "";
    setStartDate(start);
    setEndDate(end);
    
    try {
      const parts = JSON.parse(rec.Particulars);
      setParticulars(Array.isArray(parts) ? parts[0] || "" : rec.Particulars || "");
    } catch {
      setParticulars(rec.Particulars || "");
    }
    
    try {
      const hsn = JSON.parse(rec.HSN_SAC);
      setHsnSac(Array.isArray(hsn) ? hsn[0] || "" : rec.HSN_SAC || "");
    } catch {
      setHsnSac(rec.HSN_SAC || "");
    }
    
    try {
      const amt = JSON.parse(rec.Amounts);
      setAmount(Array.isArray(amt) ? amt[0] || "" : rec.Amounts || "");
    } catch {
      setAmount(rec.Amounts || "");
    }
    
    setGstRate(rec.GST_RATE || "");
    setRemarks(rec.remarks || "");
    setIsGstInclusive(rec.is_gst_inclusive === 1);
    setInvoiceNo(rec.invoice_no || "");
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !customerId || !startDate || !particulars || !amount) {
      toast.error("Please fill all required fields."); return;
    }
    setSubmitLoading(true);
    try {
      const payload = {
        Company_id: companyId,
        Customer_id: customerId,
        Particulars: JSON.stringify([particulars]),
        HSN_SAC: JSON.stringify([hsnSac]),
        Amounts: JSON.stringify([amount]),
        GST_RATE: gstRate,
        frequency,
        start_date: startDate,
        end_date: endDate || null,
        remarks,
        is_gst_inclusive: isGstInclusive ? 1 : 0,
        invoice_no: invoiceNo || null,
      };

      if (editId) {
        await axios.put(`${API_URL}/auth/recurring-invoice/${editId}`, {
          ...payload,
          is_active: recurringList.find(r => r.id === editId)?.is_active ?? 1,
        });
        toast.success("Recurring invoice updated!");
      } else {
        await axios.post(`${API_URL}/auth/recurring-invoice`, payload);
        toast.success("Recurring invoice created!");
      }
      
      setShowForm(false);
      setEditId(null);
      setCompanyId(""); setCustomerId(""); setParticulars(""); setHsnSac(""); setAmount(""); setGstRate(""); setRemarks(""); setStartDate(""); setEndDate(""); setIsGstInclusive(false); setInvoiceNo("");
      fetchData();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to save recurring invoice.");
    } finally { setSubmitLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this recurring invoice?")) return;
    try {
      await axios.delete(`${API_URL}/auth/recurring-invoice/${id}`);
      setRecurringList(prev => prev.filter(r => r.id !== id));
      toast.success("Recurring invoice deleted!");
    } catch { toast.error("Failed to delete"); }
  };

  const handleToggleActive = async (rec: any) => {
    try {
      await axios.put(`${API_URL}/auth/recurring-invoice/${rec.id}`, {
        ...rec,
        is_active: rec.is_active ? 0 : 1,
      });
      toast.success(`Recurring invoice ${rec.is_active ? "paused" : "activated"}!`);
      fetchData();
    } catch { toast.error("Failed to update"); }
  };

  const handleGenerate = async () => {
    setGenerateLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/recurring-invoice/generate`);
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error("Failed to generate invoices from recurring templates."); }
    finally { setGenerateLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto py-10 px-4">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h2 className="text-3xl font-extrabold text-blue-800">Recurring Invoices</h2>
        <div className="flex gap-3">
          <button onClick={handleGenerate} disabled={generateLoading} className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold shadow">
            {generateLoading ? "Generating..." : "⚡ Generate Due Invoices"}
          </button>
          <button onClick={() => {
            if (showForm) {
              setEditId(null);
              setCompanyId(""); setCustomerId(""); setParticulars(""); setHsnSac(""); setAmount(""); setGstRate(""); setRemarks(""); setStartDate(""); setEndDate(""); setIsGstInclusive(false); setInvoiceNo("");
            }
            setShowForm(!showForm);
          }} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow">
            {showForm ? "Cancel" : "+ New Recurring Invoice"}
          </button>
        </div>
      </div>

      {/* Toast notifications handle status and feedback messages */}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white shadow-xl border border-blue-200 rounded-2xl p-8 mb-8 space-y-6 animate__animated animate__fadeIn">
          <h3 className="text-xl font-bold text-blue-900 border-b pb-2 mb-4">
            {editId ? "✏️ Edit Recurring Invoice" : "➕ New Recurring Invoice"}
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block font-semibold text-blue-900 mb-1">Company *</label>
              <select value={companyId} onChange={e => setCompanyId(e.target.value)} required className="form-select w-full border-2 border-blue-200 rounded-xl bg-white p-2">
                <option value="">-- Select --</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-semibold text-blue-900 mb-1">Customer *</label>
              <select value={customerId} onChange={e => setCustomerId(e.target.value)} required className="form-select w-full border-2 border-blue-200 rounded-xl bg-white p-2">
                <option value="">-- Select --</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block font-semibold text-blue-900 mb-1">Frequency *</label>
              <select value={frequency} onChange={e => setFrequency(e.target.value)} className="form-select w-full border-2 border-blue-200 rounded-xl bg-white p-2">
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div>
              <label className="block font-semibold text-blue-900 mb-1">Start Date *</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required className="form-input w-full border-2 border-gray-200 rounded-xl bg-white p-2" />
            </div>
            <div>
              <label className="block font-semibold text-blue-900 mb-1">End Date (optional)</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="form-input w-full border-2 border-gray-200 rounded-xl bg-white p-2" />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block font-semibold text-blue-900 mb-1">Particulars *</label>
              <input type="text" value={particulars} onChange={e => setParticulars(e.target.value)} required className="form-input w-full border-2 border-gray-200 rounded-xl bg-white p-2" placeholder="e.g. Monthly Retainer" />
            </div>
            <div>
              <label className="block font-semibold text-blue-900 mb-1">HSN/SAC</label>
              {hsnOptions.length > 0 ? (
                <select value={hsnSac} onChange={e => setHsnSac(e.target.value)} className="form-select w-full border-2 border-gray-200 rounded-xl bg-white p-2">
                  <option value="">-- Select --</option>
                  {hsnOptions.map((h, i) => <option key={i} value={h}>{h}</option>)}
                </select>
              ) : (
                <input type="text" value={hsnSac} onChange={e => setHsnSac(e.target.value)} className="form-input w-full border-2 border-gray-200 rounded-xl bg-white p-2" placeholder="HSN/SAC code" />
              )}
            </div>
            <div>
              <label className="block font-semibold text-blue-900 mb-1">Amount (₹) *</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} required min="0" step="any" className="form-input w-full border-2 border-gray-200 rounded-xl bg-white p-2" placeholder="0.00" />
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <label className="block font-semibold text-blue-900 mb-1">GST Rate</label>
              <select value={gstRate} onChange={e => setGstRate(e.target.value)} className="form-select w-full border-2 border-green-300 rounded-xl bg-white p-2">
                <option value="">-- No GST / 0% --</option>
                {gstSlabOptions.map((r, i) => <option key={i} value={r}>{r}%</option>)}
              </select>
            </div>
            <div>
              <label className="block font-semibold text-blue-900 mb-1">Remarks</label>
              <input type="text" value={remarks} onChange={e => setRemarks(e.target.value)} className="form-input w-full border-2 border-yellow-200 rounded-xl bg-yellow-50 p-2" placeholder="Optional" />
            </div>
            <div>
              <label className="block font-semibold text-blue-900 mb-1">Custom Invoice No / Prefix</label>
              <input type="text" value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} className="form-input w-full border-2 border-gray-200 rounded-xl bg-white p-2" placeholder="e.g. REC-INV-001" />
              <p className="text-[10px] text-gray-500 mt-0.5">Increments digits on generation</p>
            </div>
            <div className="flex items-center pt-6">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isGstInclusive}
                  onChange={e => setIsGstInclusive(e.target.checked)}
                  className="w-5 h-5 rounded text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="font-semibold text-blue-900">Amount includes GST</span>
              </label>
            </div>
          </div>
          <div className="flex justify-center">
            <button type="submit" disabled={submitLoading} className="px-7 py-2.5 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold shadow-lg text-lg">
              {submitLoading ? "Saving..." : editId ? "Update Recurring Invoice" : "Create Recurring Invoice"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading...</div>
      ) : recurringList.filter(rec => userRole === "super-admin" || !userCompanyId || String(rec.Company_id) === String(userCompanyId)).length === 0 ? (
        <div className="text-center py-10 bg-white shadow rounded-2xl border text-gray-500">No recurring invoices configured.</div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-xl rounded-2xl border">
          <table className="min-w-full text-left border-collapse">
            <thead>
              <tr className="bg-blue-50 border-b">
                <th className="p-4 font-bold text-blue-900">Company</th>
                <th className="p-4 font-bold text-blue-900">Customer</th>
                <th className="p-4 font-bold text-blue-900">Invoice No</th>
                <th className="p-4 font-bold text-blue-900">Frequency</th>
                <th className="p-4 font-bold text-blue-900">GST</th>
                <th className="p-4 font-bold text-blue-900">Next Run</th>
                <th className="p-4 font-bold text-blue-900">End Date</th>
                <th className="p-4 font-bold text-blue-900">Active</th>
                <th className="p-4 font-bold text-blue-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {recurringList
                .filter(rec => userRole === "super-admin" || !userCompanyId || String(rec.Company_id) === String(userCompanyId))
                .map(rec => (
                  <tr key={rec.id} className="border-b hover:bg-blue-50/50">
                    <td className="p-4">{getCompanyName(rec.Company_id)}</td>
                    <td className="p-4">{getCustomerName(rec.Customer_id)}</td>
                    <td className="p-4 font-mono text-sm text-gray-700">{rec.invoice_no || "REC-" + rec.id + "-..."}</td>
                    <td className="p-4 capitalize font-semibold">{rec.frequency}</td>
                    <td className="p-4">
                      {rec.GST_RATE ? `${rec.GST_RATE}%` : "0%"}
                      {rec.is_gst_inclusive === 1 && (
                        <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full font-bold">Inclusive</span>
                      )}
                    </td>
                    <td className="p-4">{new Date(rec.next_run_date).toLocaleDateString("en-GB")}</td>
                    <td className="p-4">{rec.end_date ? new Date(rec.end_date).toLocaleDateString("en-GB") : "—"}</td>
                    <td className="p-4">
                      <button onClick={() => handleToggleActive(rec)} className={`px-3 py-1 rounded-full font-bold text-sm ${rec.is_active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-500"}`}>
                        {rec.is_active ? "Active" : "Paused"}
                      </button>
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(rec)} className="text-blue-500 hover:text-blue-700 font-bold bg-blue-50 px-3 py-1 rounded">Edit</button>
                        <button onClick={() => handleDelete(rec.id)} className="text-red-500 hover:text-red-700 font-bold bg-red-50 px-3 py-1 rounded">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecurringInvoices;
